/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bucketService } from '../services/api';
import toast from 'react-hot-toast';
import { Bucket, BillingPeriod } from '../types';
import { useState } from 'react';
import { AxiosError } from 'axios';

export interface UpdateBucketData {
  description?: string;
  versioning?: boolean;
  is_public?: boolean;
  max_size_gb?: number;
}

export const useBuckets = (selectedProject: string) => {
  const queryClient = useQueryClient();
  const [createError, setCreateError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isInsufficientBalance, setIsInsufficientBalance] = useState(false);
  const [balanceErrorMessage, setBalanceErrorMessage] = useState('');

  const { data: bucketsData, isLoading, isPending, isError, refetch } = useQuery({
    queryKey: ['buckets', selectedProject],
    queryFn: () => bucketService.list(selectedProject).then((res) => res.data),
    enabled: !!selectedProject,
  });

  // Process buckets data - filter out deleted buckets
  // API returns array directly in res.data, so bucketsData is already the array
  const allBuckets: Bucket[] = Array.isArray(bucketsData) ? bucketsData : [];
  const buckets = allBuckets.filter((bucket) => bucket.status !== 'deleted');

  const createMutation = useMutation({
    mutationFn: (data: {
      name: string;
      billing_period: BillingPeriod;
      versioning: boolean;
      is_public: boolean;
      description?: string;
    }) => bucketService.create(selectedProject, data),
    onMutate: () => {
      // Clear any previous error when starting a new mutation
      setCreateError(null);
      setIsInsufficientBalance(false);
      setBalanceErrorMessage('');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buckets'] });
      toast.success('Bucket created successfully');
      setCreateError(null);
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      // Check for insufficient balance error (HTTP 402)
      if (error.response?.status === 402 || error.response?.data?.error === 'insufficient_balance') {
        setIsInsufficientBalance(true);
        setBalanceErrorMessage(error.response?.data?.message || 'Insufficient wallet balance to create this resource.');
        return;
      }
      // Extract error message from API response
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to create bucket';
      setCreateError(errorMessage);
      // Don't show toast here since we're showing inline error
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBucketData }) =>
      bucketService.update(id, selectedProject, data),
    onMutate: () => {
      setUpdateError(null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buckets'] });
      toast.success('Bucket updated successfully');
      setUpdateError(null);
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to update bucket';
      setUpdateError(errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => bucketService.delete(id, selectedProject),
    onMutate: () => {
      setDeleteError(null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buckets'] });
      toast.success('Bucket deleted successfully');
      setDeleteError(null);
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to delete bucket';
      setDeleteError(errorMessage);
      // Don't show toast here since we're showing inline error in modal
    },
  });

  // Clear error functions
  const clearCreateError = () => setCreateError(null);
  const clearUpdateError = () => setUpdateError(null);
  const clearDeleteError = () => setDeleteError(null);
  const clearInsufficientBalance = () => {
    setIsInsufficientBalance(false);
    setBalanceErrorMessage('');
  };

  return {
    buckets,
    totalCount: buckets.length,
    isLoading: isLoading || isPending,
    isError,
    refetch,
    createBucket: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    createError,
    clearCreateError,
    isCreateSuccess: createMutation.isSuccess,
    updateBucket: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError,
    clearUpdateError,
    deleteBucket: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    deleteError,
    clearDeleteError,
    isInsufficientBalance,
    balanceErrorMessage,
    clearInsufficientBalance,
  };
};
